import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';
import { morphSection, sectionRenderer } from '@theme/section-renderer';

// Module state survives replacement of the upsell element during a cart morph.
let pendingUpdates = 0;
let adding = false;
let needsBasketCheck = false;

function updateButtons() {
  document.querySelectorAll('ll-cart-upsells button[type="submit"]').forEach((button) => {
    button.disabled = adding || pendingUpdates > 0 || needsBasketCheck;
  });
}

document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
  if (!event.promise) return;
  if (!adding) {
    document.querySelectorAll('[data-upsell-status]').forEach((status) => {
      // Keep the recovery link visible while an uncertain add remains locked.
      if (!needsBasketCheck) status.textContent = '';
    });
  }
  pendingUpdates++;
  updateButtons();
  const settle = () => {
    pendingUpdates--;
    requestAnimationFrame(updateButtons);
  };
  event.promise.then(settle, settle);
});

async function readCart() {
  const response = await fetch(`${Theme.routes.cart_url}.js`, {
    headers: { Accept: 'application/json' },
    cache: 'no-store',
    signal: AbortSignal.timeout(15000),
  });
  if (!response.ok) throw new Error('Unable to read basket');
  return response.json();
}

class LLCartUpsells extends HTMLElement {
  connectedCallback() {
    this.addEventListener('submit', this.onSubmit);
    updateButtons();
  }

  disconnectedCallback() {
    this.removeEventListener('submit', this.onSubmit);
  }

  onSubmit = async (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    event.preventDefault();
    if (adding || pendingUpdates > 0 || needsBasketCheck) return;

    const owner = this.closest('cart-items-component');
    const drawer = this.closest('cart-drawer-component');
    const status = drawer?.querySelector('[data-upsell-status]');
    if (!owner || !drawer || !status) return;

    const sectionId = owner.dataset.sectionId;
    const variantId = new FormData(form).get('id');
    if (!sectionId || !variantId) return;
    const productId = form.closest('[data-product-id]')?.dataset.productId;
    const focusedElement = document.activeElement;
    const shouldRestoreFocus = form.contains(focusedElement);
    const messages = { ...this.dataset };
    const deferred = CartLinesUpdateEvent.createPromise();
    // A rejected event must still be handled when no other cart component is mounted.
    deferred.promise.catch(() => {});
    adding = true;
    status.textContent = '';
    owner.inert = true;
    owner.setAttribute('aria-busy', 'true');
    updateButtons();

    // This owner performs its own awaited morph. Its native event handler skips
    // self-originated events; header/count/other cart components still receive it.
    owner.dispatchEvent(new CartLinesUpdateEvent({
      action: 'add',
      context: 'cart',
      lines: [{ merchandiseId: String(variantId), quantity: 1 }],
      promise: deferred.promise,
    }));

    let confirmed = false;
    let didError = false;
    let message = '';
    let sections;
    try {
      try {
        const sectionIds = [...new Set([sectionId, ...Array.from(
          document.querySelectorAll('cart-items-component[data-section-id]'),
          (element) => element.dataset.sectionId
        )])].slice(0, 5);
        const response = await fetch(Theme.routes.cart_add_url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            items: [{ id: String(variantId), quantity: 1 }],
            sections: sectionIds,
            sections_url: window.location.pathname,
          }),
          signal: AbortSignal.timeout(15000),
        });
        const result = await response.json();
        confirmed = response.ok || response.status === 422;
        if (!response.ok || result.status) {
          didError = true;
          message = typeof result.description === 'string' ? result.description : messages.error;
        } else {
          sections = result.sections;
        }
      } catch {
        didError = true;
      }

      // A failed/ambiguous POST may have changed the cart. Read before allowing
      // further action; never automatically retry an add request.
      const cart = await readCart();
      if (sections?.[sectionId]) {
        await morphSection(sectionId, sections[sectionId], { mode: 'hydration' });
      } else {
        const html = await sectionRenderer.getSectionHTML(sectionId, false, new URL(window.location.href), AbortSignal.timeout(15000));
        await morphSection(sectionId, html, { mode: 'hydration' });
      }
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { items: cart.items, sections, didError, source: 'll-cart-upsells' },
      });
      needsBasketCheck = !confirmed;
      message = confirmed ? (didError ? message || messages.error : messages.added) : messages.uncertain;
    } catch (error) {
      // Keep retries locked if the actual basket or refreshed markup is unknown.
      needsBasketCheck = true;
      message = messages.uncertain;
      deferred.reject(error);
    } finally {
      adding = false;
      const currentOwner = drawer.querySelector('cart-items-component');
      owner.inert = false;
      owner.removeAttribute('aria-busy');
      if (currentOwner) {
        currentOwner.inert = false;
        currentOwner.removeAttribute('aria-busy');
      }
      const currentStatus = drawer.querySelector('[data-upsell-status]');
      if (currentStatus) {
        currentStatus.textContent = message;
        if (needsBasketCheck) {
          const link = document.createElement('a');
          link.href = Theme.routes.cart_url;
          link.textContent = messages.checkBasket;
          currentStatus.append(' ', link);
        }
      }
      updateButtons();
      // Restore keyboard focus after the added card disappears, but never reopen
      // a closed drawer or take focus away from a visitor who moved it elsewhere.
      if (shouldRestoreFocus && drawer.closest('dialog')?.open &&
          (!document.activeElement || document.activeElement === document.body || document.activeElement === focusedElement)) {
        const target = drawer.querySelector(`li[data-product-id="${productId}"] .ll-cart-upsells__action:not(:disabled)`) ||
          drawer.querySelector('.ll-cart-upsells__action:not(:disabled)') ||
          drawer.querySelector('[name="checkout"]');
        target?.focus({ preventScroll: true });
      }
    }
  };
}

if (!customElements.get('ll-cart-upsells')) {
  customElements.define('ll-cart-upsells', LLCartUpsells);
}
